import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'
import {type AppBskyFeedDefs} from '@atproto/api'

import {TapApiClient, type TapPostsResponse} from '#/lib/api/tap'
import {useAgent, useSession} from '#/state/session'
import {STALE} from '.'

const tapPostsQueryKeyRoot = 'tap-posts'
const tapPostsQueryKey = (did: string, tagSlug: string) => [tapPostsQueryKeyRoot, did, tagSlug]

export function useTapPostsQuery(
  tagSlug: string,
  opts?: {enabled?: boolean},
) {
  const {currentAccount} = useSession()
  const agent = useAgent()
  const accessToken = currentAccount?.accessJwt
  const did = currentAccount?.did ?? 'guest'

  return useInfiniteQuery<
    TapPostsResponse,
    Error,
    InfiniteData<TapPostsResponse>,
    QueryKey,
    number
  >({
    enabled: opts?.enabled !== false,
    queryKey: tapPostsQueryKey(did, tagSlug),
    queryFn: async ({pageParam}) => {
      const client = new TapApiClient()
      const response = await client.getPostsByTag({
        slug: tagSlug,
        limit: 50,
        offset: pageParam,
        accessToken, // Pass user's Bluesky access token
      })

      // Fetch complete post data from Bluesky for ALL posts
      // This gives us: correct engagement metrics, viewer state, video playlist URLs, and author info
      if (response.posts && response.posts.length > 0 && agent) {
        try {
          const postUris = response.posts.map(post => post.uri)

          console.log('[useTapPostsQuery] Fetching complete post data from Bluesky:', {
            tag: tagSlug,
            count: postUris.length,
          })

          // Fetch posts in batches of 25 (API limit)
          const batchSize = 25
          const postBatches = []
          for (let i = 0; i < postUris.length; i += batchSize) {
            const batch = postUris.slice(i, i + batchSize)
            postBatches.push(agent.getPosts({uris: batch}))
          }

          const batchResults = await Promise.all(postBatches)
          const blueskyPosts = batchResults.flatMap(r => r.data.posts)

          // Create a map of URI to complete post data
          const blueskyPostMap = new Map(
            blueskyPosts.map(p => [p.uri, p]),
          )

          // Update ALL posts with complete data from Bluesky
          for (let i = 0; i < response.posts.length; i++) {
            const tapPost = response.posts[i]
            const blueskyPost = blueskyPostMap.get(tapPost.uri)

            if (blueskyPost) {
              // Merge: Use Bluesky's complete data, but preserve TAP extensions
              response.posts[i] = {
                ...blueskyPost,
                // Preserve TAP-specific fields
                hasImages: tapPost.hasImages,
                hasVideo: tapPost.hasVideo,
                hasGif: tapPost.hasGif,
                imageCount: tapPost.imageCount,
                tags: tapPost.tags,
                tagsComplete: tapPost.tagsComplete,
              }
            }
          }

          console.log('[useTapPostsQuery] Fetched complete post data:', {
            tag: tagSlug,
            requested: postUris.length,
            fetched: blueskyPosts.length,
            samplePost: blueskyPosts[0] ? {
              uri: blueskyPosts[0].uri.slice(-15),
              likeCount: blueskyPosts[0].likeCount,
              hasViewer: !!blueskyPosts[0].viewer,
              viewerLike: blueskyPosts[0].viewer?.like,
            } : null,
          })
        } catch (error) {
          console.error('[useTapPostsQuery] Failed to fetch complete post data:', error)
        }
      }

      // Log the response to see what we're getting
      console.log('[useTapPostsQuery] Got response:', {
        postsCount: response.posts?.length,
        firstPostHasAuthor: response.posts?.[0]?.author ? true : false,
        firstPostAuthorDid: response.posts?.[0]?.author?.did,
      })

      return response
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * 50
      return currentOffset < lastPage.total ? currentOffset : undefined
    },
    staleTime: STALE.SECONDS.THIRTY,
  })
}

export function* findAllPostsInQueryData(
  queryClient: QueryClient,
  uri: string,
): Generator<AppBskyFeedDefs.PostView, void> {
  const queryDatas = queryClient.getQueriesData<
    InfiniteData<TapPostsResponse>
  >({
    queryKey: [tapPostsQueryKeyRoot],
  })
  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData?.pages) {
      continue
    }
    for (const page of queryData.pages) {
      for (const post of page.posts) {
        if (post.uri === uri) {
          yield post
        }
      }
    }
  }
}
